import type { SettingsTranslations } from "../interfaces/settings";

export const settingsTranslations: SettingsTranslations = {
  title_page: "Settings",
  language_label: "Language",
  general_settings_title: "General",
  domain_settings_title: "Domain",
  integrations_settings_title: "Integrations",

  // Page-level
  settings_eyebrow_account: "Account",
  settings_subtitle: "Theme, language and security of your Coloca payments account",
  settings_nav_on_this_page: "On this page",
  settings_group_preferences: "Preferences",
  settings_group_security: "Security",
  settings_group_developers: "Developers",
  settings_group_alerts: "Alerts",
  settings_group_account: "Account",

  // Sections
  appearance_section_title: "Appearance",
  appearance_section_description: "Customize the application interface",
  language_section_title: "Language & region",
  language_section_description: "Set the interface language and the timezone used in your reports.",
  security_section_title: "Security",
  security_section_description: "Protect your account with two-factor authentication",

  // Devices
  devices_section_title: "Active sessions",
  devices_section_eyebrow: "Devices",
  devices_section_subtitle: "Devices where your account is currently signed in.",
  devices_badge_current: "CURRENT",
  devices_action_revoke: "Sign out",
  devices_action_revoke_others: "Sign out all other sessions",
  devices_confirm_revoke_title: "Sign out this session?",
  devices_confirm_revoke_body:
    "{{device}} will lose access immediately. You'll need to sign in again from that device.",
  devices_confirm_revoke: "Sign out",
  devices_confirm_revoke_others_title: "Sign out every other session?",
  devices_confirm_revoke_others_body:
    "You'll close {{n}} session(s) on other devices. Your current session stays signed in.",
  devices_confirm_revoke_others: "Sign others out",
  devices_confirm_cancel: "Cancel",
  devices_last_seen_now: "Just now",
  devices_last_seen_minutes: "{{n}} min ago",
  devices_last_seen_hours: "{{n}} h ago",
  devices_last_seen_days: "{{n}} days ago",
  devices_revoke_success: "Session signed out",
  devices_revoke_others_success: "{{n}} session(s) signed out",
  devices_revoke_error: "Could not sign the session out. Please try again.",

  // Appearance
  theme_label: "Theme",
  theme_light: "Light",
  theme_dark: "Dark",
  appearance_theme_card_title: "Application theme",
  appearance_theme_card_sub:
    "Affects background, text and surface colors. System mode follows your device preference.",
  appearance_theme_light_hint: "Best for natural light",
  appearance_theme_dark_hint: "Reduces eye strain",
  appearance_theme_system_label: "System",
  appearance_theme_system_hint: "Follows the operating system",

  // Language & region
  language_eyebrow: "Localization",
  language_interface_label: "Interface language",
  language_interface_hint: "Applies to menus, messages and receipts.",
  language_timezone_label: "Timezone",
  language_timezone_hint: "Used in history, reports and scheduled notifications. Saved to your profile.",
  language_timezone_save_success: "Timezone updated",
  language_country_label: "Country",
  language_country_hint: "Account country.",
  language_country_save_success: "Country updated",
  language_country_placeholder: "Select a country",
  language_country_search_placeholder: "Search country…",
  language_country_no_results: "No results",

  // Security - 2FA
  twofa_status_enabled: "ENABLED",
  twofa_status_disabled: "DISABLED",
  twofa_enable_button: "Enable",
  twofa_disable_button: "Disable",
  twofa_description: "Two-factor authentication",
  security_2fa_title: "Two-step verification (2FA)",
  security_2fa_sub:
    "Required on every sign-in and to confirm transfers above the limit set by your organization.",
  security_2fa_status_not_configured: "NOT CONFIGURED",
  security_2fa_protected_title: "Your account is protected with 2FA",
  security_2fa_protected_sub: "Primary method: authenticator app (TOTP).",
  security_2fa_unprotected_title: "Your account does not have 2FA",
  security_2fa_unprotected_sub: "Set it up to comply with your organization’s security policy.",
  security_2fa_pick_method: "Pick a primary method",
  security_method_totp_name: "Authenticator app",
  security_method_totp_sub: "Google Authenticator, 1Password, Authy…",
  security_method_email_name: "Email",
  security_method_email_sub: "Receive a 6-digit code at your registered email.",
  security_method_tag_recommended: "RECOMMENDED",
  security_method_tag_coming_soon: "COMING SOON",
  security_2fa_totp_setup_title: "Link authenticator app",
  security_2fa_totp_step: "STEP {{step}} OF {{total}}",
  security_2fa_code_valid_30s: "code changes every 30s",
  security_hide: "Hide",
  security_show: "Show",
  security_recovery_title: "Recovery codes",
  security_recovery_sub:
    "Keep them somewhere safe. Each code can be used once if you lose access to your primary method.",
  security_recovery_regenerate: "Regenerate",
  security_recovery_download: "Download .txt",
  security_recovery_copy_all: "Copy all",
  security_recovery_copied: "Copied",
  security_recovery_regenerated: "New codes generated",
  security_recovery_warning_title: "Copy them now: they are shown only once",
  security_recovery_warning_body:
    "Once you leave this page we won’t be able to show them again. Save them in your password manager, or wherever you prefer.",
  security_recovery_empty_hint:
    "Codes are generated automatically when you enable 2FA. If you did not save them, regenerate to get a fresh set.",

  // Enable 2FA Modal
  enable_twofa_title: "Enable two-factor authentication",
  enable_twofa_scan_instructions:
    "Scan this QR code with your authentication app (Google Authenticator, Authy, etc.)",
  enable_twofa_manual_entry: "Or manually enter this secret code:",
  enable_twofa_enter_code: "Enter the 6-digit code from your app",
  enable_twofa_verify_button: "Verify and enable",
  enable_twofa_cancel_button: "Cancel",
  enable_twofa_success: "2FA enabled successfully",

  // Disable 2FA Modal
  disable_twofa_title: "Disable two-factor authentication",
  disable_twofa_description: "To disable 2FA, enter your current authentication code",
  disable_twofa_warning: "Warning: This will reduce your account security",
  disable_twofa_verify_button: "Disable 2FA",
  disable_twofa_cancel_button: "Cancel",
  disable_twofa_success: "2FA disabled successfully",

  // Common
  copy_button: "Copy",
  copied_message: "Copied to clipboard",
  copy_error_message: "Couldn’t copy to the clipboard",
  error_invalid_code: "Invalid code. Please try again.",
  error_generic: "An error occurred. Please try again later.",

  // API Keys
  api_keys_empty: "No API Keys created for this organization yet.",
  api_keys_docs_button: "API docs",
  api_keys_enable_action: "Enable",
  api_keys_disable_action: "Disable",
  api_keys_edit_ips_button: "Edit IPs",
  api_keys_disable_title: "Disable this API key?",
  api_keys_disable_subtitle: "Reversible action — you can re-enable it anytime.",
  api_keys_disable_confirm: "Yes, disable",
  api_keys_disable_cancel: "Cancel",
  api_keys_disable_success: "API Key disabled",
  api_keys_enable_success: "API Key enabled",
  api_keys_disable_impact_title: "Integrations using this key will stop working",
  api_keys_disable_impact_unauthorized: "All API requests with this key will return 401 Unauthorized.",
  api_keys_disable_impact_integrations: "Services that depend on it (backend, jobs, etc.) will stop.",
  api_keys_disable_impact_reversible: "You can re-enable it anytime — it is not deleted nor rotated.",
  api_keys_edit_ips_title: "Allowed IPs",
  api_keys_edit_ips_description: "When IPs are allowed, requests from other addresses are rejected with",
  api_keys_edit_ips_placeholder: "54.213.10.45",
  api_keys_edit_ips_add: "Add",
  api_keys_edit_ips_remove: "Remove",
  api_keys_edit_ips_save: "Save",
  api_keys_edit_ips_cancel: "Cancel",
  api_keys_edit_ips_list_label: "Allowed IPs",
  api_keys_no_ips_hint: "No IP restriction — the key works from any address.",
  api_keys_invalid_ip: "Invalid format. Use an IPv4 (e.g. 54.213.10.45) or CIDR (e.g. 10.0.0.0/24).",
  api_keys_ip_duplicate: "That IP is already in the list.",
  api_keys_update_success: "API Key updated",
  api_keys_admin_no_ip_warning:
    "This is an ADMIN key with no IP restriction — we strongly recommend limiting it to your production backend IPs.",
  api_keys_add_ip_label: "Add IP or range (CIDR)",
  api_keys_ip_label_placeholder: "Label (optional)",
  api_keys_any_ip_pill: "Any IP",
  api_keys_ip_entry_singular: "entry",
  api_keys_ip_entry_plural: "entries",
  api_keys_your_current_ip_is: "Your current IP is",
  api_keys_add_my_ip: "Add",
  api_keys_my_ip_label_default: "My current IP",
  api_keys_ip_added: "Added",
  api_keys_my_ip_detecting: "Detecting your current IP…",
  api_keys_my_ip_unavailable: "We couldn't auto-detect your current IP. You can type it manually above.",
  api_keys_create_button: "Create API key",
  api_keys_create_title: "Create API key",
  api_keys_create_name_label: "Name",
  api_keys_create_name_hint: "To identify it in logs and audit.",
  api_keys_create_name_placeholder: "e.g. Production backend",
  api_keys_create_scopes_label: "Permissions",
  api_keys_create_scopes_hint: "Select only the minimum necessary.",
  api_keys_create_scopes_selected: "{{count}} selected",
  api_keys_create_quick_readonly: "Read-only",
  api_keys_create_quick_all: "All",
  api_keys_create_quick_none: "None",
  api_keys_create_group_select_all: "All",
  api_keys_create_group_clear: "Clear",
  api_keys_create_advanced: "Advanced settings",
  api_keys_create_advanced_hint: "IP restrictions",
  api_keys_create_ip_section_label: "IP restriction (allowlist)",
  api_keys_create_once_hint: "You'll see the key only once after creating it.",
  api_keys_create_submit: "Create key",
  api_keys_create_cancel: "Cancel",
  api_keys_create_success: "API key created.",
  api_keys_create_error: "Could not create the API key.",
  api_keys_create_name_required: "Enter a name for the key.",
  api_keys_create_scopes_required: "Select at least one permission.",
  api_keys_created_banner_title: "Copy your API key now",
  api_keys_created_banner_warning:
    "For security, this key is shown only once. You won't be able to see it again.",
  api_keys_created_copy: "Copy",
  api_keys_created_copied: "Copied",
  api_keys_created_dismiss: "I saved it",
  api_keys_no_write_title: "API key creation is restricted",
  api_keys_no_write_description:
    "To enable key creation we need to validate your use case. Write to us at {{email}} and we'll activate it for your organization.",
  api_keys_scope_group_identities: "Holders",
  api_keys_scope_group_payments: "Payments",
  api_keys_scope_group_wallets: "Wallets",
  api_keys_scope_group_cards: "Cards",
  api_keys_scope_group_webhooks: "Webhooks",
  api_keys_scope_identities_read: "View holders",
  api_keys_scope_identities_write: "Create and edit holders",
  api_keys_scope_payments_read: "View payments and quotes",
  api_keys_scope_payments_write: "Create payins and payouts",
  api_keys_scope_wallets_read: "View balances and statements",
  api_keys_scope_wallets_write: "Activate and manage wallets",
  api_keys_scope_cards_read: "View cards",
  api_keys_scope_cards_write: "Create and manage cards",
  api_keys_scope_cards_topup: "Top up cards",
  api_keys_scope_cards_transfer: "Transfer between cards",
  api_keys_scope_webhooks_read: "View webhook settings and deliveries",
  api_keys_scope_webhooks_write: "Configure the webhook endpoint",
  relative_time_now: "just now",
  relative_time_minutes: "{{n}} min ago",
  relative_time_hours: "{{n}} h ago",
  relative_time_days: "{{n}} d ago",

  // Access & permissions (RBAC)
  settings_group_access: "Access & permissions",
  access_users_nav_label: "Users",
  access_eyebrow: "Access & permissions",
  access_users_title: "Users",
  access_users_sub:
    "Team members. Each one is granted roles that determine their effective access, resolved deny-first.",
  access_stat_active: "Active",
  access_stat_no_roles: "No roles",
  access_search_person_placeholder: "Search person or email",
  access_filter_all_roles: "All roles",
  access_stat_total: "Total",
  access_filter_all_statuses: "All statuses",
  access_status_active: "Active",
  access_status_inactive: "Inactive",
  access_status_suspended: "Suspended",
  access_status_pending_verification: "Pending verification",
  access_role_filter_placeholder: "Filter by role",
  access_search_min_hint: "Type at least {{min}} characters to search",
  access_col_person: "Person",
  access_col_roles: "Assigned roles",
  access_col_effective: "Effective access",
  access_col_actions: "Actions",
  access_badge_you: "YOU",
  access_badge_external: "EXT",
  access_badge_pending: "PENDING",
  access_badge_no_roles: "NO ROLES",
  access_badge_api_key: "API KEY",
  access_badge_roles_unavailable: "ROLES UNAVAILABLE",
  access_roles_overflow_action: "View all roles ({{count}} more)",
  access_roles_overflow_more: "Additional roles: {{count}}",
  access_effective_unknown_title:
    "Couldn't load this entry's roles — effective access is unknown, not confirmed as none.",
  access_effective_catalog_unknown_title:
    "The roles catalog is unavailable — effective access is unknown, not confirmed as none.",
  access_catalog_unavailable:
    "The roles catalog couldn't be loaded (requires the roles:read permission). Effective access can't be computed and role management is disabled; assigned roles are shown for information only.",
  access_roles_partial_fail:
    "Couldn't load roles for {{count}} entries. Their access is shown as unavailable (unknown), not as no-access.",
  access_action_roles: "Roles",
  access_action_view: "View",
  access_users_footnote:
    "Effective access is the net result of combining every role and resolving the policies deny-first.",
  access_users_load_failed: "The users could not be loaded.",
  access_no_results: "No results.",
  access_no_access_title: "You don't have permission to manage access",
  access_no_access_description:
    "Your role doesn't include the required permission. Ask an admin to grant you access to users and roles.",
  access_readonly_note: "With your role you can view access but not change it.",
  access_view_as_label: "Preview as",
  access_view_as_editor: "Editor",
  access_view_as_viewer: "Read-only",
  access_view_as_demo: "DEMO · this session only",
  access_effective_granted_title: "permissions granted",
  access_effective_denied_title: "active denies",

  // Access — invite person
  access_invite_action: "Invite person",
  access_invite_title: "Invite person",
  access_invite_sub:
    "The person will receive an email with a single-use link to create their account and join your organization.",
  access_invite_email_label: "Email",
  access_invite_email_placeholder: "person@company.com",
  access_invite_email_required: "Email is required.",
  access_invite_email_invalid: "Enter a valid email.",
  access_invite_field_max: "Maximum 255 characters.",
  access_invite_name_label: "Name (optional)",
  access_invite_name_placeholder: "First and last name",
  access_invite_language_label: "Invitation language",
  access_invite_language_hint: "Language the invitation email will be sent in.",
  access_invite_cancel: "Cancel",
  access_invite_submit: "Send invitation",
  access_invite_success: "Invitation sent to {{email}}.",
  access_invite_roles_label: "Access they start with",
  access_invite_roles_hint:
    "Read-only permissions come pre-ticked. Add the write ones they need and those are assigned the moment they accept.",
  access_invite_roles_search: "Search by name or identifier",
  access_invite_roles_clear: "Clear all",
  access_invite_roles_no_matches: "No role matches the search",
  access_invite_roles_global: "Global",
  access_invite_roles_none_selected: "No roles: they will land read-only",
  access_invite_roles_count: "{{count}} roles selected",
  access_invite_access_note_with_roles:
    "Accepting the invitation assigns the {{count}} selected roles. You can adjust them afterwards from the roles catalog in this same section.",
  access_invite_access_note:
    "The person will start with read-only access. Once they accept the invitation they will appear in this table and you can assign the roles they need.",

  // Access — API keys as principals
  access_apikeys_nav_label: "API Keys",
  access_apikeys_title: "API Keys",
  access_apikeys_sub:
    "Integration keys are principals just like people: they receive the same roles and share the deny-first permission evaluator.",
  access_apikeys_stat_keys: "Keys",
  access_apikeys_truncated:
    "Showing the first {{shown}} of {{total}} keys. Use search to find a specific key; the count, simulator and per-role members reflect only the keys shown.",
  access_apikeys_search_placeholder: "Search key",
  access_col_key: "Key",
  access_apikeys_footnote:
    "API keys receive roles just like people; effective access is the result of resolving their policies deny-first.",
  access_apikeys_admin_badge: "ADMIN",
  access_apikeys_super_badge: "SUPER",
  access_apikeys_inactive: "INACTIVE",
  access_apikeys_more_actions: "More actions",
  access_apikeys_ip_count_singular: "1 IP",
  access_apikeys_ip_count_plural: "{{count}} IPs",
  access_apikeys_any_ip: "any IP",

  // Access — assignment drawer + auto-save + matrix
  access_drawer_apply_immediately: "Changes are saved automatically.",
  access_drawer_readonly: "Read-only.",
  access_drawer_close: "Close",
  access_drawer_roles_count: "Roles · {{count}} assigned",
  access_drawer_no_roles_available: "No roles available.",
  access_drawer_search_roles_placeholder: "Search role",
  access_drawer_no_role_matches: "No role matches the search.",
  access_drawer_group_broad: "Broad access",
  access_drawer_group_org: "Organization roles",
  access_drawer_group_empty: "Grants nothing",
  access_drawer_group_unknown: "Uncatalogued",
  access_drawer_deny_notice:
    "There are {{count}} active deny policy(ies). These block permissions even if another role grants them.",
  access_drawer_effective_title: "Resulting effective access",
  access_drawer_effective_hint: "* = deny wins over allow",
  access_drawer_no_roles_empty: "No roles — no access at all (default deny).",
  access_saved_saving: "Saving…",
  access_saved_done: "Changes saved automatically",
  access_saved_error: "Couldn't save the changes",

  // Access — friendly labels for the real catalog (backend Module)
  access_mod_users: "Users",
  access_mod_api_key: "API keys",
  access_mod_roles: "Roles",
  access_mod_policies: "Policies",
  access_mod_organizations: "Organizations",
  access_mod_organization_accounts: "Organization accounts",
  access_mod_identities: "Identities (KYC)",
  access_mod_wallets: "Wallets",
  access_mod_wallets_transfer_sensitive: "Sensitive wallet transfer",
  access_mod_cards: "Cards",
  access_mod_cards_topup: "Card top-ups",
  access_mod_cards_transfer: "Card transfers",
  access_mod_cards_sensitive: "Sensitive card data",
  access_mod_cards_topup_sensitive: "Sensitive card top-up",
  access_mod_fees: "Fees",
  access_mod_fee_configurations: "Fee configuration",
  access_mod_payments: "Payments runtime",
  access_mod_payout_batches: "Payout batches",
  access_mod_payout_batches_approve: "Payout batch approval",
  access_mod_clients: "Clients",
  access_mod_client_accounts: "Client accounts",
  access_mod_operations: "Operations",
  access_mod_operations_payments: "Payments / Dispersions",
  access_mod_operations_confirmations: "Confirmations",
  access_mod_operations_notes: "Operation notes",
  access_mod_operations_shipping_quotes: "Shipping quotes",
  access_mod_operations_shipping_quotes_coloca: "Coloca quotes",
  access_mod_operations_audit: "Audit",
  access_mod_operations_public_accounts: "Public accounts",
  access_mod_operations_notifications: "Notifications",

  // Access — Roles
  access_scope_global: "GLOBAL",
  access_scope_org: "ORG",
  access_roles_nav_label: "Roles",
  access_roles_title: "Roles",
  access_roles_sub:
    "A role bundles policies and is assigned to users and API keys. Global roles are managed by Coloca.",
  access_roles_create: "Create role",
  access_roles_group_global: "Global roles",
  access_roles_group_global_note: "Managed by Coloca",
  access_roles_group_org: "Organization roles",
  access_roles_group_org_note: "Editable and deletable",
  access_roles_group_org_empty: "You haven't created any roles yet",
  access_roles_empty_group: "No roles here.",
  access_page_refresh: "Refresh",
  access_page_clear_filters: "Clear filters",
  access_page_scope_label: "Scope",
  access_page_scope_all: "All",
  access_page_col_actions: "Actions",
  access_page_col_scope: "Scope",
  access_page_empty_filtered_body: "Try another term or change the filters.",
  access_roles_search_placeholder: "Search by name, slug or description",
  access_roles_kpi_group_label: "Roles catalog summary",
  access_roles_kpi_total: "Roles",
  access_roles_kpi_deny: "With deny",
  access_roles_kpi_deny_meta: "They take access away",
  access_roles_kpi_no_policies: "No policies",
  access_roles_kpi_no_policies_meta: "They grant nothing",
  access_roles_kpi_no_members: "Unassigned",
  access_roles_col_role: "Role",
  access_roles_col_policies: "Policies",
  access_roles_col_reach: "Reach",
  access_roles_col_members: "Assignments",
  access_roles_no_policies_badge: "No policies",
  access_roles_empty_title: "No roles yet",
  access_roles_empty_body: "Create the first one to start grouping policies so they can be assigned.",
  access_roles_empty_filtered_title: "No role matches",
  access_roles_protected_hint: "System role, read-only",
  access_roles_readonly_note: "You can browse the catalog, but not create or edit roles.",
  access_policies_search_placeholder: "Search by name, description or permission (resource:action)",
  access_policies_kpi_group_label: "Policies catalog summary",
  access_policies_kpi_total: "Policies",
  access_policies_kpi_allow: "Allow",
  access_policies_kpi_deny: "Deny",
  access_policies_kpi_deny_meta: "They win over any allow",
  access_policies_kpi_unused: "Unused",
  access_policies_kpi_unused_meta: "No role links them",
  access_policies_col_policy: "Policy",
  access_policies_col_effect: "Effect",
  access_policies_col_permissions: "Permissions",
  access_policies_col_used_by: "Used by",
  access_policies_unused_badge: "Unused",
  access_policies_usage_unknown_title:
    "We could not read the roles catalog, so we do not know whether any role links this policy.",
  access_policies_usage_unknown_meta: "Unknown",
  access_policies_usage_unavailable:
    "Without access to the roles catalog we cannot tell which policies are linked, so deleting is unavailable.",
  access_policies_empty_title: "No policies yet",
  access_policies_empty_body: "Create the first one to group permissions so a role can link them.",
  access_policies_empty_filtered_title: "No policy matches",
  access_policies_protected_hint: "System policy, read-only",
  access_policies_readonly_note: "You can browse the catalog, but not create or edit policies.",
  access_apikeys_kpi_group_label: "API key catalog summary",
  access_apikeys_kpi_total: "Keys",
  access_apikeys_kpi_active: "Active",
  access_apikeys_kpi_no_roles: "No roles",
  access_apikeys_kpi_no_roles_meta: "They authorize nothing",
  access_apikeys_kpi_no_roles_partial: "At least: some roles unread",
  access_apikeys_kpi_not_active: "Inactive or expired",
  access_apikeys_col_organization: "Organization",
  access_apikeys_col_status: "Status",
  access_apikeys_status_active: "Active",
  access_apikeys_status_expired: "Expired",
  access_apikeys_status_inactive: "Inactive",
  access_apikeys_org_global: "Global",
  access_apikeys_truncated_page:
    "Showing the first {{shown}} of {{total}} keys. This page's search, filters and figures describe only the keys that were loaded.",
  access_apikeys_empty_title: "No API keys yet",
  access_apikeys_empty_body: "Create the first one so an integration can authenticate.",
  access_apikeys_empty_filtered_title: "No key matches",
  access_apikeys_empty_filtered_body: "Try another term or change the role filter.",
  access_role_default_badge: "DEFAULT",
  access_role_policy_count_singular: "1 policy",
  access_role_policy_count_plural: "{{count}} policies",
  access_role_has_deny: "with deny",
  access_role_editor_new: "Create role",
  access_role_editor_new_sub: "Define a custom role by bundling policies.",
  access_role_editor_edit: "Edit role",
  access_role_editor_view: "View role",
  access_role_name_label: "Role name",
  access_role_name_placeholder: "e.g. Payments approver",
  access_role_name_required: "Enter a name for the role.",
  access_role_desc_label: "Description",
  access_role_desc_placeholder: "What this role is for.",
  access_role_policies_label: "Role policies",
  access_role_policies_allow: "Allow",
  access_role_policies_deny: "Deny",
  access_role_policies_deny_hint: "These win over any allow in the role.",
  access_role_policies_none_available: "None available.",
  access_role_effective_title: "Resulting effective access",
  access_role_no_policies_empty: "No policies — no access at all (default deny).",
  access_role_readonly_note: "Global or protected role managed by Coloca · read-only.",
  access_role_save: "Save",
  access_role_create_action: "Create role",
  access_role_cancel: "Cancel",
  access_role_delete: "Delete",
  access_role_delete_title: "Delete role?",
  access_role_delete_desc:
    "The role “{{name}}” will be removed from any user or API key using it. This can't be undone.",
  access_role_delete_confirm: "Delete role",
  access_role_created: "Role created.",
  access_role_updated: "Role updated.",
  access_role_deleted: "Role deleted.",
  access_role_member_count_platform_one: "1 across the platform",
  access_role_member_count_platform: "{{count}} across the platform",
  access_role_member_count_org_one: "1 in your organization",
  access_role_member_count_org: "{{count}} in your organization",
  access_role_members_users_label: "Users with this role",
  access_role_members_keys_note:
    "API keys also hold this role; they cannot be listed by role, but they count towards the total.",
  access_role_members_unavailable: "The user list could not be loaded.",
  access_role_members_label: "Principals using this role",
  access_role_no_members: "No principal uses this role yet.",
  access_role_members_more: "+{{count}} more",
  access_role_delete_desc_members:
    "The role “{{name}}” will be removed from {{count}} principal(s) using it. This can't be undone.",

  // Access — editor validation (shared by role + policy editors; limits mirror the backend DTOs)
  access_validation_name_max: "120 characters maximum.",
  access_validation_desc_max: "500 characters maximum.",

  // Access — Policies
  access_policy_effect_allow: "ALLOW",
  access_policy_effect_deny: "DENY",
  access_policies_nav_label: "Policies",
  access_policies_title: "Policies",
  access_policies_sub:
    "A policy bundles resource:action permissions under a single effect. Deny policies always win over allow.",
  access_policies_create: "New policy",
  access_policies_group_allow: "Allow",
  access_policies_group_allow_note: "Grant access to resources.",
  access_policies_group_deny: "Deny",
  access_policies_group_deny_note: "Block access even if another grants it.",
  access_policies_empty_group: "No policies in this group.",
  access_policy_stmt_summary: "{{permits}} permissions · {{resources}} resources",
  access_policy_used_by_singular: "used by 1 role",
  access_policy_used_by_plural: "used by {{count}} roles",
  access_policy_editor_new: "New policy",
  access_policy_editor_new_sub: "Define a reusable permission bundle.",
  access_policy_editor_edit: "Edit policy",
  access_policy_editor_view: "View policy",
  access_policy_name_label: "Name",
  access_policy_name_placeholder: "e.g. Payments · Operations",
  access_policy_name_required: "Enter a name for the policy.",
  access_policy_name_taken: "A policy with that name already exists.",
  access_policy_desc_label: "Description",
  access_policy_desc_placeholder: "What this policy grants or denies.",
  access_policy_effect_label: "Effect",
  access_policy_effect_allow_title: "Allow",
  access_policy_effect_allow_hint: "Grants access",
  access_policy_effect_deny_title: "Deny",
  access_policy_effect_deny_hint: "Blocks — wins over allow",
  access_policy_statements_label: "Permissions in this policy",
  access_policy_statements_hint: "resource : action",
  access_policy_statements_required: "Select at least one permission.",
  access_policy_permits_singular: "1 permission",
  access_policy_permits_plural: "{{count}} permissions",
  access_policy_readonly_note: "Global or protected policy managed by Coloca · read-only.",
  access_policy_save: "Save",
  access_policy_create_action: "Create policy",
  access_policy_cancel: "Cancel",
  access_policy_delete: "Delete",
  access_policy_delete_disabled_hint: "Remove it from roles before deleting.",
  access_policy_delete_title: "Delete policy?",
  access_policy_delete_desc: "The policy “{{name}}” will be deleted. This can't be undone.",
  access_policy_delete_confirm: "Delete policy",
  access_policy_delete_in_use:
    "Can't delete: the policy is in use by one or more roles. Remove it from them first.",
  access_policy_created: "Policy created.",
  access_policy_updated: "Policy updated.",
  access_policy_deleted: "Policy deleted.",

  // Access — Simulator
  access_sim_nav_label: "Simulator",
  access_sim_title: "Access simulator",
  access_sim_sub:
    "Check why a principal can or cannot perform an action — which role and policy grant it, and whether a deny is overriding it.",
  access_sim_principal_label: "Principal",
  access_sim_principal_placeholder: "Select a principal",
  access_sim_no_principals: "No principals (users or API keys) to simulate.",
  access_sim_page_scope_note:
    "You can simulate the {{count}} principals on the current Users page plus the loaded API keys. Filter or page that table to reach anyone else.",
  access_sim_selection_dropped:
    "The principal you were simulating is no longer on the current page. Pick it again or adjust the Users filters.",
  access_sim_partial_principals:
    "{{count}} entries were excluded from the simulator because their roles couldn't be loaded.",
  access_sim_matrix_hint: "Click a permission to inspect it",
  access_sim_pick_prompt: "Select a permission in the matrix to see how it resolves.",
  access_sim_explanation_title: "Explanation",
  access_sim_result_allow: "ALLOWED",
  access_sim_result_deny: "DENIED",
  access_sim_result_none: "DENIED (default)",
  access_sim_reason_allowed_by: "Allowed by",
  access_sim_reason_denied_by: "Denied by",
  access_sim_reason_overridden: "Allow overridden (no effect)",
  access_sim_summary_allow: "granted by an allow policy",
  access_sim_summary_deny: "blocked by a deny policy",
  access_sim_summary_deny_wins: "deny wins over {{count}} allow",
  access_sim_summary_none: "no allow matches",
  access_sim_none_detail:
    "No policy assigned to {{name}} grants {{perm}}. By the default-deny rule, without an explicit allow access is denied.",
  access_sim_rule:
    "Rule: any deny wins first; with no deny, a single allow suffices; with none, access is denied by default.",
  access_sim_via_policy: "policy {{policy}}",
  access_mod_organization_webhooks: "Webhooks",

  // Webhooks (outbound, Developers group)
  webhooks_nav_label: "Webhooks",
  webhooks_section_eyebrow: "Developers",
  webhooks_section_title: "Outbound webhooks",
  webhooks_section_sub:
    "Coloca notifies your server every time a payin, payout or swap changes status. One HTTPS endpoint receives every event.",
  webhooks_load_error: "We could not load the webhook settings.",
  webhooks_retry_load: "Retry",
  webhooks_empty_title: "You are not receiving events yet",
  webhooks_empty_desc:
    "Register your server's HTTPS URL. Every delivery carries the x-secret header so you can check it comes from Coloca.",
  webhooks_add_endpoint: "Add endpoint",
  webhooks_endpoint_label: "Endpoint",
  webhooks_secret_label: "Secret",
  webhooks_secret_header_hint: "Sent in the {{header}} header of every delivery",
  webhooks_status_active: "Active",
  webhooks_status_paused: "Paused",
  webhooks_status_auto_disabled: "Disabled after failures",
  webhooks_disabled_reason_streak:
    "We paused deliveries after 50 consecutive failures from your server. Fix it, test the connection and turn the endpoint back on.",
  webhooks_disabled_reason_prolonged:
    "We paused deliveries after 24 hours without a successful response from your server. Fix it, test the connection and turn the endpoint back on.",
  webhooks_paused_hint:
    "While paused, no new deliveries are queued. The connection test keeps working so you can verify the fix.",
  webhooks_health_failures: "Consecutive failures",
  webhooks_health_last_success: "Last success",
  webhooks_health_last_failure: "Last failure",
  webhooks_health_never: "Never",
  webhooks_toggle_aria: "Turn deliveries on or off",
  webhooks_action_edit_url: "Change URL",
  webhooks_action_test: "Send test event",
  webhooks_action_rotate: "Rotate secret",
  webhooks_action_delete: "Remove endpoint",
  webhooks_toast_saved: "Endpoint saved",
  webhooks_toast_paused: "Deliveries paused",
  webhooks_toast_resumed: "Deliveries resumed",
  webhooks_toast_rotated: "Secret rotated",
  webhooks_toast_deleted: "Endpoint removed",
  webhooks_toast_resend_queued: "Re-send queued as a new delivery",
  webhooks_toast_retry_queued: "Delivery queued again",
  webhooks_error_no_write_permission: "Your role cannot change the webhook settings.",
  webhooks_read_only_notice:
    "Your role can only view webhooks. To configure them, ask an administrator of your organization to assign you the webhook write role in Settings → Users.",
  webhooks_url_dialog_title_create: "Add endpoint",
  webhooks_url_dialog_title_edit: "Change the endpoint URL",
  webhooks_url_dialog_desc:
    "It must be HTTPS and answer 2xx within 10 seconds. On failure we retry up to 5 times with growing delays.",
  webhooks_url_field_label: "Your server URL",
  webhooks_url_field_placeholder: "https://api.yourcompany.com/coloca/webhooks",
  webhooks_url_invalid: "Enter a valid HTTPS URL.",
  webhooks_url_save: "Save endpoint",
  webhooks_url_register_and_verify: "Register and verify",
  webhooks_url_cancel: "Cancel",
  webhooks_secret_field_label: "Shared secret",
  webhooks_secret_field_hint_create:
    "We send it in the {{header}} header of every event. Use the one your server already expects, or leave it blank and we generate it.",
  webhooks_secret_field_hint_edit:
    "Leave it blank to keep the current secret. Type a new one and it starts travelling in {{header}} immediately.",
  webhooks_secret_field_placeholder: "At least 16 characters, no spaces",
  webhooks_secret_generate: "Generate one",
  webhooks_secret_invalid: "Use at least {{min}} printable characters, with no spaces.",
  webhooks_handshake_explainer:
    "On registration we send a configuration handshake to your server. If it answers 2xx we enable deliveries; if not, the endpoint stays paused and we show you what it answered.",
  webhooks_toast_saved_paused: "Endpoint saved and paused",
  webhooks_toast_handshake_ok: "Your server answered: deliveries enabled",
  webhooks_toast_handshake_failed: "Endpoint saved paused: your server did not accept the handshake",
  webhooks_secret_banner_title: "Save this secret now",
  webhooks_secret_banner_warning:
    "It is shown only once. Compare it with the {{header}} header of every delivery to confirm it comes from Coloca.",
  webhooks_secret_copy: "Copy",
  webhooks_secret_copied: "Copied",
  webhooks_secret_dismiss: "I saved it",
  webhooks_secret_reveal: "Reveal",
  webhooks_secret_hide: "Hide",
  webhooks_error_no_reveal_permission: "Your role cannot see the full secret.",
  webhooks_rotate_title: "Rotate the secret?",
  webhooks_rotate_desc:
    "Deliveries switch to the new secret immediately. Update your server in the same window or it will reject the events.",
  webhooks_rotate_confirm: "Rotate secret",
  webhooks_delete_title: "Remove the endpoint?",
  webhooks_delete_desc:
    "We stop sending events. The delivery history is kept and you can register another endpoint at any time.",
  webhooks_delete_confirm: "Remove endpoint",
  webhooks_handshake_verified_title: "Endpoint verified in {{ms}} ms",
  webhooks_handshake_verified_detail:
    "Your server returned the right secret, so we confirmed the endpoint is yours. It can start receiving events.",
  webhooks_handshake_no_response_title: "Your server did not answer",
  webhooks_handshake_no_response_detail:
    "We got no response at all: {{error}}. Check that the URL is reachable from the internet, that the certificate is valid, and that it answers within 10 seconds.",
  webhooks_handshake_http_error_title: "Your server rejected the event",
  webhooks_handshake_http_error_detail:
    "It answered HTTP {{status}} in {{ms}} ms. We need a 2xx to consider the endpoint verified.",
  webhooks_handshake_invalid_response_title: "The response is not JSON",
  webhooks_handshake_invalid_response_detail:
    "Your server answered fine, but the body is not a JSON object. Return the value of the {{header}} header in a field called secret:",
  webhooks_handshake_secret_missing_title: "The response carries no secret",
  webhooks_handshake_secret_missing_detail:
    "Your server answered 200 without returning the secret, and without it any URL that answers would pass the test. Return the value of the {{header}} header in a field called secret:",
  webhooks_handshake_secret_mismatch_title: "The secret does not match",
  webhooks_handshake_secret_mismatch_detail:
    "Your server returned a secret different from the configured one. Compare it with the {{header}} header of the request you just received, or rotate the secret if yours is out of date:",
  webhooks_test_hint: "The test is logged in the delivery report as Test · Ping.",
  webhooks_no_response: "no response",
  webhooks_deliveries_title: "Deliveries",
  webhooks_deliveries_sub:
    "Every attempt with its HTTP code and latency. Retry the ones that failed once your server is ready.",
  webhooks_deliveries_refresh: "Refresh",
  webhooks_deliveries_error: "We could not load the deliveries.",
  webhooks_deliveries_empty_title: "No deliveries yet",
  webhooks_deliveries_empty_desc:
    "They will show up here when something happens to your payments, identities, cards or wallets, or when you send a test event.",
  webhooks_deliveries_empty_filtered: "No delivery matches the filters",
  webhooks_filter_status: "Status",
  webhooks_filter_type: "Type",
  webhooks_filter_event: "Event",
  webhooks_filter_status_code: "HTTP code",
  webhooks_filter_range: "Dates",
  webhooks_filter_all: "All",
  webhooks_filter_apply: "Apply filters",
  webhooks_filter_clear: "Clear filters",
  webhooks_th_created: "Date",
  webhooks_th_event: "Event",
  webhooks_th_status: "Status",
  webhooks_th_http: "HTTP",
  webhooks_th_attempts: "Attempts",
  webhooks_th_latency: "Latency",
  webhooks_status_pending: "In progress",
  webhooks_status_delivered: "Delivered",
  webhooks_status_failed: "Rejected",
  webhooks_status_exhausted: "Out of retries",
  webhooks_type_payments: "Payments",
  webhooks_type_identities: "Identities",
  webhooks_type_cards: "Cards",
  webhooks_type_wallets: "Wallets",
  webhooks_type_test: "Test",
  webhooks_event_payin_created: "Payin created",
  webhooks_event_payin_pending: "Payin pending",
  webhooks_event_payin_approved: "Payin approved",
  webhooks_event_payin_confirmed: "Payin confirmed",
  webhooks_event_payin_settled: "Payin settled",
  webhooks_event_payin_rejected: "Payin rejected",
  webhooks_event_payin_reversed: "Payin reversed",
  webhooks_event_payout_created: "Payout created",
  webhooks_event_payout_processing: "Payout processing",
  webhooks_event_payout_paid: "Payout paid",
  webhooks_event_payout_failed: "Payout failed",
  webhooks_event_payout_cancelled: "Payout cancelled",
  webhooks_event_payout_reversed: "Payout reversed",
  webhooks_event_swap_completed: "Swap completed",
  webhooks_event_identity_created: "Identity created",
  webhooks_event_kyc_approved: "Verification approved",
  webhooks_event_kyc_rejected: "Verification rejected",
  webhooks_event_cardholder_created: "Cardholder created",
  webhooks_event_card_created: "Card created",
  webhooks_event_card_status_changed: "Card status changed",
  webhooks_event_card_transaction_authorized: "Card transaction authorized",
  webhooks_event_card_transaction_declined: "Card transaction declined",
  webhooks_event_card_transaction_refunded: "Card transaction refunded",
  webhooks_event_card_transaction_reversed: "Card transaction reversed",
  webhooks_event_card_topped_up: "Card topped up",
  webhooks_event_card_deposit_received: "Crypto deposit received",
  webhooks_event_card_withdrawn: "Withdrawal from card",
  webhooks_event_card_wallet_recharged: "Card wallet recharged",
  webhooks_event_wallet_created: "Wallet created",
  webhooks_event_ping: "Ping",
  webhooks_endpoint_disabled_error: "The endpoint was paused or removed when this attempt ran",
  webhooks_drawer_title: "Delivery detail",
  webhooks_drawer_id: "Delivery ID",
  webhooks_drawer_event_id: "Resource ID",
  webhooks_drawer_url: "Sent to",
  webhooks_drawer_created: "Created",
  webhooks_drawer_delivered: "Delivered",
  webhooks_drawer_last_error: "Last error",
  webhooks_drawer_payload: "Body sent",
  webhooks_drawer_response: "Your server's response",
  webhooks_drawer_response_truncated: "We keep the first 2 KB of the response.",
  webhooks_drawer_response_empty: "No response body",
  webhooks_drawer_retry: "Retry delivery",
  webhooks_drawer_resend: "Re-send delivery",
  webhooks_drawer_resend_hint:
    "This delivery already arrived. Re-sending it creates a new delivery with a different event id, so your server will process it a second time.",
  webhooks_drawer_retry_hint:
    "This delivery is still being attempted. Wait for it to finish before re-queuing it.",
  webhooks_guide_title: "How to connect your server",
  webhooks_guide_sub:
    "How to set up the secret, the events we send, the handshake contract, and what happens when you rotate or change the URL",
  webhooks_guide_setup_title: "Store the secret in your server",
  webhooks_guide_setup_body:
    "The handshake runs the moment you register the URL, so the secret has to be stored in your server before you register it. Otherwise the first verification fails and the endpoint lands paused.",
  webhooks_guide_setup_own:
    "Bring your own secret (recommended): paste the value your server already holds into the form's Shared secret field. The endpoint then activates on the first attempt.",
  webhooks_guide_setup_generated:
    "Or let us generate it: copy it from the banner — it is shown only once — store it in your server, send a test event and activate the endpoint.",
  webhooks_guide_setup_format:
    "Format: 16 to 255 printable ASCII characters, no spaces. Keep it as an environment variable or a secret, never in your code.",
  webhooks_guide_receive_title: "Receive the event",
  webhooks_guide_receive_body:
    "We send a POST with a JSON body that always has the same shape: route on type (the group) and event (what happened); data is the resource exactly as its public GET /:id endpoint returns it. The secret travels in the {{header}} header on every delivery, not only on the test.",
  webhooks_guide_receive_dedupe:
    "Deduplicate on id: it stays the same across our retries of a delivery. Re-sending an already delivered event from this screen creates a new id.",
  webhooks_guide_receive_snapshot:
    "data is a snapshot from the moment of the event. If you need the resource as it is now, read it again with GET.",
  webhooks_guide_receive_order:
    "Events can arrive out of order: compare created_at, and accept an event about a resource you have not seen yet.",
  webhooks_guide_handshake_title: "Echo the secret back on the test event",
  webhooks_guide_handshake_body:
    "When the event has type test and event ping, answer 200 with JSON carrying back the value of the {{header}} header in a field called secret. Without that echo we do not activate the endpoint: any URL can answer 200, and that proves nothing about who is listening. Every other event needs only a 2xx.",
  webhooks_guide_verify_title: "Validate the secret on every request",
  webhooks_guide_verify_body:
    "On your side, compare the {{header}} header against the secret you stored and reject with 401 if it does not match, before doing any work. That comparison is what stops somebody else from injecting fake events.",
  webhooks_guide_catalog_title: "Events we send",
  webhooks_guide_catalog_body: "Pick a group to see its events and an example of the body you receive.",
  webhooks_guide_catalog_kyb:
    "kyc_approved and kyc_rejected also cover business verification (KYB): tell them apart with data.type.",
  webhooks_guide_catalog_events: "Events",
  webhooks_guide_catalog_example: "Example",
  webhooks_guide_rotation_title: "Rotating the secret and changing the URL",
  webhooks_guide_rotation_badge: "Pauses deliveries",
  webhooks_guide_rotation_rotate:
    "Rotating pauses the endpoint immediately and re-runs the handshake. If your server keeps answering with the previous secret, it stays disabled until you update it.",
  webhooks_guide_rotation_url:
    "Changing the URL behaves the same way: it is saved paused, we run the handshake against the new address, and only then is it switched back on.",
  webhooks_guide_rotation_activate:
    "Flipping the switch on also requires the handshake. If your server does not echo the secret, the activation is refused and the endpoint is left as it was.",
  webhooks_guide_rotation_order:
    "So the order matters: update the secret on your server first, then send a test event from here.",
  webhooks_guide_retries_title: "Retries and failures",
  webhooks_guide_retries_body:
    "You get 10 seconds per attempt. A 5xx, a 408, a 429 or a timeout is retried up to 5 times with growing waits of 1, 2, 4 and 8 minutes. Any other 4xx counts as a permanent rejection and is not retried. If your endpoint piles up failures we email you, and if it stays down we pause it ourselves.",
  webhooks_activate_blocked: "Send a test event and pass the handshake before activating the endpoint.",
  webhooks_disabled_reason_handshake:
    "We paused deliveries because your server did not return the right secret after the last change. Update it, send a test event and switch it back on.",
  webhooks_toast_rotated_paused:
    "Secret rotated and deliveries paused: update your server, then switch the endpoint back on",
  webhooks_toast_saved_revalidation_failed:
    "Endpoint saved and paused: your server did not return the secret after the change",
  webhooks_copy_aria: "Copy",
  webhooks_copy_success: "Copied to clipboard",

  // Account → Verification
  verification_nav_label: "Verification",
  card_wallet_nav_label: "Card wallet",
  card_wallet_section_eyebrow: "Cards",
  card_wallet_section_title: "Card wallet",
  card_wallet_section_sub:
    "A dollar balance of your organization that connects your cards: they load from it, and what you take back from them returns to it.",
  card_wallet_status_on: "On",
  card_wallet_status_off: "Off",
  card_wallet_compare_without: "Without the wallet",
  card_wallet_compare_with: "With the wallet",
  card_wallet_row_load: "Load cards",
  card_wallet_row_load_without: "From pesos or by sending crypto",
  card_wallet_row_load_with: "Also from the wallet, instantly",
  card_wallet_row_withdraw: "Take balance back from a card",
  card_wallet_row_withdraw_without: "Not possible: it can only move to another card",
  card_wallet_row_withdraw_with: "Yes: it returns to the wallet",
  card_wallet_activate: "Learn more and turn on",
  card_wallet_permanent_note: "Once it's on, only support can turn it off.",
  card_wallet_on_body:
    "Your cards load from the wallet instantly, and what you take back from them returns to it.",
  card_wallet_go_to_cards: "Go to Cards",
  card_wallet_on_note: "To turn it off, contact support.",
  verification_section_title: "Account verification",
  verification_section_sub:
    "Your KYC or KYB verification defines which operations you can perform. Start it, resume it and review what you submitted here.",
  verification_empty_title: "You have not verified your account yet",
  verification_empty_description:
    "Choose whether you operate as a natural person (KYC) or as a company (KYB) and complete the verification to enable every operation.",
  verification_type_label: "Verification type",
  verification_type_person: "Natural person (KYC)",
  verification_type_business: "Company (KYB)",
  verification_status_label: "Status",
  verification_verified_at: "Approved on {{date}}",
  verification_pending_hint: "Not completed yet. Resume it where you left off.",
  verification_in_review_hint: "Under review. We will email you as soon as we have the result.",
  verification_expired_hint: "The verification expired. Start it again to keep operating.",
  verification_copy_link: "Copy verification link",
  verification_copy_link_hint:
    "Send it to whoever completes the verification, for example the legal representative.",
  verification_data_title: "Submitted data",
  verification_data_note:
    "These are the data the verification validated. To correct them, or to verify the account as another person or company, use “Verify with another identity”.",
  verification_field_name: "Name",
  verification_field_legal_name: "Legal name",
  verification_field_document: "Document",
  verification_field_tax_id: "Tax ID",
  verification_field_country: "Country",
  verification_field_birthdate: "Date of birth",
  verification_field_nationality: "Nationality",
  verification_field_business_type: "Company type",
  verification_field_incorporation_date: "Incorporation date",
  verification_field_representative: "Legal representative",
  verification_field_representative_document: "Representative's document",
  verification_field_address: "Address",
  verification_field_representative_address: "Representative's address",
  verification_capabilities_title: "What you can do",
  verification_change_title: "Change identity",
  verification_change_hint:
    "Verify the account as another person or company, or switch between natural person and company.",
  verification_change_identity: "Verify with another identity",
  verification_change_dialog_title: "Verify with another identity?",
  verification_change_dialog_description:
    "The current verification will no longer apply to your account and you will not be able to send money until the new verification is approved. You will choose again between natural person and company.",
  verification_change_dialog_confirm: "Yes, use another identity",
  verification_change_dialog_cancel: "Cancel",
  verification_change_cooldown_hint:
    "You replaced a verified identity recently. Available again from {{date}}.",
  verification_change_request: "Request an early change",
  verification_change_request_sent: "Request sent. Operations will review your case.",
  verification_error_load: "We could not load your verification status.",
  verification_retry: "Retry",
  verification_data_unavailable: "We could not load the verification data. Try again later.",
  verification_copy_link_unavailable:
    "A new link was issued, but it could not be copied to the clipboard. Try again from a supported browser.",
  security_pin_status_badge: "PIN ACTIVE",
  security_pin_active_title: "Your account is protected with a security PIN",
  security_pin_active_sub:
    "We'll ask for it on sensitive actions. For more security, switch to the authenticator app.",
  security_pin_change: "Change PIN",
  security_pin_remove: "Remove PIN",
  security_pin_locked_title: "Your PIN is locked",
  security_pin_locked_temporary: "Too many wrong attempts. It unlocks in {{time}}, or create a new one now.",
  security_pin_locked_reset: "Too many wrong attempts. Create a new PIN with your password to keep using it.",
  security_pin_locked_action: "Create new PIN",
  security_totp_replaces_pin:
    "The authenticator app protects your sensitive actions and replaces the security PIN.",
  security_totp_will_replace_pin:
    "Once you verify the code, the app replaces your PIN: from then on we'll ask for the app's code.",
  security_upgrade_pick: "Want more security?",
  security_method_totp_sub_upgrade:
    "Safer than the PIN: the code lives on your phone and changes every 30 s.",
  security_method_tag_recommended_secure: "RECOMMENDED · MOST SECURE",
  security_method_pin_name: "Security PIN",
  security_method_pin_sub: "Six digits you choose. Ready in a minute, though less secure than the app.",
  security_method_pin_sub_active: "It's your current method.",
  security_method_tag_active: "ACTIVE",
  security_method_tag_fastest: "FASTEST",
  security_methods_compare_note:
    "The authenticator app is the most secure option: the code changes every 30 seconds and only lives on your phone. The PIN is faster to set up; if you start with it, you can switch to the app any time.",
  security_pin_setup_title: "Create security PIN",
  security_pin_created_toast: "Your security PIN is on",
  security_pin_changed_toast: "Your new PIN is saved",
  security_pin_removed_toast: "You removed your security PIN",
  security_pin_remove_title: "Remove your security PIN?",
  security_pin_remove_body:
    "Without a PIN or the authenticator app you won't be able to create cards, view their details or move money until you set one of them up.",
  security_pin_remove_confirm: "Remove PIN",
  security_pin_remove_cancel: "Cancel",
  security_totp_password_intro:
    "First confirm it's you: enter your password and we'll generate the QR code for your app.",
  security_totp_password_submit: "Generate QR code",
  security_totp_password_required: "Enter your password.",
  security_totp_locked_title: "Confirmation paused",
  security_totp_locked: "There were 5 wrong codes in a row. You can confirm actions again in {{time}}.",
  security_email_backup_eyebrow: "Plan B",
  security_email_backup_title: "Email backup",
  security_email_backup_sub_pin_on:
    "On: if you forget your PIN or it's locked, we email you a code to confirm the action.",
  security_email_backup_sub_pin_off:
    "Off: if you forget your PIN, you'll need to create a new one with your password before you continue.",
  security_email_backup_sub_totp_off:
    "It's a bit less secure than using only the app: anyone who gets into your email could also confirm actions. Turn it on only if you want a plan B.",
  security_email_backup_sub_totp_on:
    "On: if you don't have your phone at hand, we email you a code. Anyone who gets into your email could also confirm actions.",
  security_email_backup_sub_none:
    "It becomes available once you choose the authenticator app or a PIN: it's that method's plan B, not a method on its own.",
  security_email_backup_destination: "Codes go to {{email}}",
  security_email_backup_tag_on: "On",
  security_email_backup_tag_off: "Off",
  security_email_backup_tag_unavailable: "Not available yet",
  security_email_backup_switch_label: "Turn the email backup on or off",
  security_email_backup_enable_title: "Turn on the email backup",
  security_email_backup_disable_title: "Turn off the email backup",
  security_email_backup_enable_body_pin:
    "If you ever forget your PIN or it gets locked, you'll be able to ask for a code at {{email}} to confirm the action.",
  security_email_backup_enable_body_totp:
    "If you don't have your phone at hand, you'll be able to ask for a code at {{email}}. Keep in mind that anyone who gets into your email could also confirm actions.",
  security_email_backup_disable_body:
    "You'll only be able to confirm actions with your main method. If you lose it, you'll have to recover it before you continue.",
  security_email_backup_password_intro: "Confirm with your password to save the change.",
  security_email_backup_cancel: "Cancel",
  security_email_backup_enable_confirm: "Turn on backup",
  security_email_backup_disable_confirm: "Turn off backup",
  security_email_backup_enabled_toast: "Done: the email backup is on.",
  security_email_backup_disabled_toast: "Done: the email backup is off.",
  security_guide_title: "Which one should I choose?",
  security_guide_totp_label: "Authenticator app",
  security_guide_totp: "The most secure: a new code on your phone every 30 seconds.",
  security_guide_pin_label: "Security PIN",
  security_guide_pin: "The fastest: six digits you choose, ready in a minute.",
  security_guide_email_label: "Email backup",
  security_guide_email: "Plan B: a code to your email when you don't have your PIN or your phone at hand.",
  security_method_pin_email_note: "Includes an email backup",
  security_method_totp_email_note: "Optional email backup",
};
